import type {
  ToolCallMessagePartComponent,
  ToolCallMessagePartStatus,
} from '@assistant-ui/react';
import {Icon, type IconName} from '@wandb/components';
import type {Color} from '@wandb/components/dist/theme/color';
import React, {memo} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Button,
  Disclosure,
  DisclosurePanel,
  Heading,
} from 'react-aria-components';

import {useNightModeRevertFilter} from './useNightModeRevertFilter';

const statusConfig: Record<
  ToolCallMessagePartStatus['type'] | 'error',
  {icon: IconName; color: Color}
> = {
  running: {icon: 'loading', color: 'moon'},
  complete: {icon: 'checkmark-circle', color: 'green'},
  incomplete: {icon: 'failed-error-deleted', color: 'red'},
  error: {icon: 'failed-error-deleted', color: 'red'},
  'requires-action': {icon: 'warning', color: 'sienna'},
};

const ToolCallTrigger: React.FC<{
  toolName: string;
  status: ToolCallMessagePartStatus;
  isError?: boolean;
}> = ({toolName, status, isError}) => {
  const statusType = isError ? 'error' : status.type;
  const config = statusConfig[statusType];
  return (
    <Heading className="m-0">
      <Button
        slot="trigger"
        className="relative flex w-full gap-8 space-between items-center self-stretch bg-transparent px-12 cursor-pointer text-moon-800">
        <span
          className={
            config.icon === 'loading'
              ? 'inline-flex animate-spin [animation-duration:4s]'
              : 'inline-flex'
          }>
          <Icon icon={config.icon} color={config.color} />
        </span>
        <span className="grow text-left leading-[21px]">{toolName}</span>
        <span className="inline-flex rotate-0 transition-[rotate] duration-[220ms] group-data-[expanded]/tool-call:rotate-90">
          <Icon icon="chevron-next" size="sm" />
        </span>
      </Button>
    </Heading>
  );
};

const ToolCallArgs: React.FC<{argsText?: string}> = ({argsText}) => {
  if (!argsText) {
    return null;
  }
  return (
    <div className="px-12">
      <pre className="whitespace-pre-wrap text-xs text-moon-500">
        {argsText}
      </pre>
    </div>
  );
};

const ToolCallResult: React.FC<{result?: unknown}> = ({result}) => {
  if (result === undefined) {
    return null;
  }
  return (
    <div className="border-t border-dashed border-moon-250 px-12 pt-8">
      <p className="font-semibold text-xs text-moon-650">Result:</p>
      <pre className="whitespace-pre-wrap text-xs text-moon-500">
        {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
};

const ToolCallError: React.FC<{
  status: ToolCallMessagePartStatus;
}> = ({status}) => {
  if (status.type !== 'incomplete') {
    return null;
  }
  const error = status.error;
  const errorText = error
    ? typeof error === 'string'
      ? error
      : JSON.stringify(error)
    : null;
  if (!errorText) {
    return null;
  }
  return (
    <div className="px-12">
      <p className="font-semibold text-xs text-moon-500">
        {status.reason === 'cancelled' ? 'Cancelled reason:' : 'Error:'}
      </p>
      <p className="text-moon-500">{errorText}</p>
    </div>
  );
};

const ToolCallImpl: ToolCallMessagePartComponent = ({
  toolName,
  argsText,
  result,
  status,
  isError,
}: {
  toolName: string;
  argsText?: string;
  result?: unknown;
  status: ToolCallMessagePartStatus;
  isError?: boolean;
}) => {
  const isCancelled =
    status.type === 'incomplete' && status.reason === 'cancelled';
  const nightModeRevertFilter = useNightModeRevertFilter();

  return (
    <Disclosure defaultExpanded={false} className="group/tool-call">
      <div
        className={`relative w-full col-auto gap-8 rounded-lg border border-moon-250 py-8 ${
          isCancelled ? 'opacity-60' : ''
        }`}>
        <div
          className={'absolute inset-0 rounded-lg dark:bg-moon-900'}
          style={{filter: nightModeRevertFilter}}
        />
        <ToolCallTrigger
          toolName={toolName}
          status={status}
          isError={isError}
        />
        <DisclosurePanel className="relative h-[--disclosure-panel-height] overflow-clip transition-[height] duration-200">
          <div className="relative mt-8 flex flex-col gap-8 border-t border-moon-250 pt-8">
            <ToolCallError status={status} />
            <ToolCallArgs argsText={argsText} />
            {!isCancelled && <ToolCallResult result={result} />}
          </div>
        </DisclosurePanel>
      </div>
    </Disclosure>
  );
};

export const WBAgentToolCall = memo(
  ToolCallImpl
) as ToolCallMessagePartComponent;
WBAgentToolCall.displayName = 'WBAgentToolCall';
